import {plansForVariant, PACK_HANDLES} from './nurox-pdp-buybox.js';

// Deliberately not imported. A sibling import resolves to the CDN asset URL without the ?v=
// cache buster the page uses for the entry module, so the browser can be handed a stale copy of
// nurox-pdp-buybox.js (formerly nurox-pdp-commerce.js). Importing a newly added export from it takes the whole cart module down
// with a SyntaxError until that cache expires, which is exactly what happened on 2026-09-17.
// These sizes track PACK_HANDLES and change about as often, so a local copy is the safer trade.
const PACK_SIZES = [3, 5, 10];
// The PDP sells rolls (one roll = one 5 Pack) with tier discounts at 2 and 3 rolls, applied by
// Shopify automatic discounts. A bag holding rolls is offered another roll, never the 10 Pack.
const ROLL_HANDLE = 'nurox-focus-pouches'; // the 5 Pack, on the main URL since 2026-10-14
const ROLL_NEXT = {1: 'Unlocks 10% off', 2: 'Unlocks 20% off'};

// Section HTML is replaced after every cart action. Module evaluation and delegation run once.
const route = window.Shopify?.routes?.root || '/';
const products = new Map();
let busy = false;
const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
  style:'currency', currency:window.Shopify?.currency?.active || 'USD'
}).format(cents / 100);
async function json(url, options) {
  const response = await fetch(route + url, options);
  const result = await response.json();
  if (!response.ok || result.status >= 400) throw new Error(result.description || result.message || 'Could not update your bag. Please try again.');
  return result;
}
function product(handle) {
  if (!products.has(handle)) products.set(handle, json(`products/${encodeURIComponent(handle)}.js`).catch(error=>{products.delete(handle);throw error;}));
  return products.get(handle);
}
function sections() {
  return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el=>el.dataset.sectionId))].slice(0,5);
}
async function change(body) {
  return json('cart/change.js?appstle=true', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({...body,sections:sections(),sections_url:location.pathname})});
}
async function add(item) {
  const body=new FormData();
  for(const [key,value] of Object.entries(item)) {
    if(key==='properties') { for(const [name,val] of Object.entries(value || {})) body.append(`properties[${name}]`,val); }
    else if(value !== undefined) body.append(key,String(value));
  }
  body.append('sections',sections().join(','));body.append('sections_url',location.pathname);
  return json('cart/add?appstle=true',{method:'POST',headers:{Accept:'application/javascript','X-Requested-With':'XMLHttpRequest'},body});
}
function paint(cart) {
  for(const [id,html] of Object.entries(cart.sections || {})) {
    if(!html) continue;
    const parsed=new DOMParser().parseFromString(html,'text/html');
    for(const target of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if(target.dataset.sectionId!==id) continue;
      const replacement=[...parsed.querySelectorAll('cart-items-component')].find(el=>el.dataset.sectionId===id);
      if(replacement) target.innerHTML=replacement.innerHTML;
    }
  }
  (document.querySelector('cart-items-component') || document).dispatchEvent(new CustomEvent('cart:update',{bubbles:true,detail:{resource:cart,sourceId:'nurox-cart',data:{source:'nurox-cart',itemCount:cart.item_count,sections:cart.sections || {}}}}));
}
function button(text, action, values={}) {
  const el=document.createElement('button');el.type='button';el.className='nxc__action';el.textContent=text;el.dataset.cartAction=action;
  Object.assign(el.dataset,values);return el;
}
function notice(message) {
  document.querySelectorAll('[data-nurox-cart-status]').forEach(el=>el.textContent=message);
}
async function mountLine(el) {
  if(el.dataset.mounted) return;el.dataset.mounted='true';
  try {
    const p=await product(el.dataset.handle);const variant=p.variants.find(v=>String(v.id)===el.dataset.variant);
    const plans=plansForVariant(p,variant);if(!plans.length || !el.isConnected) return;
    const current=plans.find(p=>p.id===el.dataset.plan);
    const selected=current || plans.find(p=>p.weeks===4) || plans[0];
    // Subscribing is one way. Once the line carries a plan the switch is gone for good and only
    // the cadence stays editable, so the bag can never drop back to one time by accident.
    if(current) {
      const select=document.createElement('select');select.className='nxc__cadence';select.setAttribute('aria-label',`Subscription delivery for ${variant.title}`);
      for(const plan of plans) select.add(new Option(`${plan.name} · ${money(plan.price)}`,plan.id,false,plan.id===current.id));
      select.dataset.cartAction='cadence';
      const details=document.createElement('div');details.className='nxc__subscription-details';
      const hint=document.createElement('small');hint.textContent='Skip or cancel anytime.';
      details.append(select,hint);el.replaceChildren(details);
      return;
    }
    const label=`Subscribe & Save ${money(variant.price-selected.price)}`;
    const toggle=button('', 'subscription',{key:el.dataset.key,plan:selected.id});toggle.className='nxc__sub-toggle';toggle.setAttribute('aria-pressed','false');
    const text=document.createElement('span');text.className='nxc__sub-toggle-text';text.textContent=label;
    const cadence=document.createElement('span');cadence.className='nxc__sub-toggle-sub';cadence.textContent=Number.isFinite(selected.weeks)?` · every ${selected.weeks} wks`:` · ${selected.name}`;text.append(cadence);
    const icon=document.createElement('span');icon.className='nxc__sub-toggle-icon';icon.setAttribute('aria-hidden','true');icon.innerHTML='<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>';
    const track=document.createElement('span');track.className='nxc__sub-switch';track.setAttribute('aria-hidden','true');
    const thumb=document.createElement('span');thumb.className='nxc__sub-switch-thumb';track.append(thumb);toggle.append(icon,text,track);
    el.replaceChildren(toggle);
  } catch {el.dataset.mounted='';}
}
async function mountOffers(el) {
  if(el.dataset.mounted) return;el.dataset.mounted='true';
  try {
    // A shelf copied in by a drawer re-render would otherwise stack on top of the one we build.
    el.querySelectorAll('.nxc__cross').forEach(node=>node.remove());
    const cart=await json('cart.js');const source=cart.items.find(i=>PACK_HANDLES.includes(i.handle));
    if(!source || !el.isConnected) return;
    const sourcePlan=source.selling_plan_allocation?.selling_plan;
    const flavor=source.variant_title;
    const next=PACK_HANDLES[PACK_HANDLES.indexOf(source.handle)+1];
    // Both upsells share June's compact card: 52px thumbnail, two short lines, price in the button.
    let wrap=null;
    function shelf() {
      if(!wrap){wrap=document.createElement('div');wrap.className='nxc__cross';const heading=document.createElement('div');heading.className='nxc__cross-head';heading.textContent='Complete your bag';wrap.append(heading);el.append(wrap);}
      return wrap;
    }
    const rolls=cart.items.filter(i=>i.handle===ROLL_HANDLE).reduce((sum,i)=>sum+i.quantity,0);
    function offer(p,v,action) {
      // The bag never mixes modes: once a line is on a plan, every offer carries that same plan.
      const preserveSubscription=!!sourcePlan;
      if(!v?.available || (!preserveSubscription && v.requires_selling_plan))return;
      const plan=preserveSubscription?plansForVariant(p,v).find(plan=>plan.name===sourcePlan.name):null;
      if(preserveSubscription&&!plan)return;
      const price=plan?.price ?? v.price;
      const cans=PACK_SIZES[PACK_HANDLES.indexOf(p.handle)];
      const card=document.createElement('div');card.className='nxc__cross-card';
      const imageUrl=v.featured_image?.src || p.featured_image;
      if(imageUrl){const image=document.createElement('img');const url=new URL(imageUrl,location.origin);url.searchParams.set('width','160');image.src=url.href;image.alt='';image.width=52;image.height=52;image.loading='lazy';image.className='nxc__cross-img';card.append(image);}
      const info=document.createElement('div');info.className='nxc__cross-info';
      const isRoll=p.handle===ROLL_HANDLE&&action==='cross';
      const title=document.createElement('div');title.className='nxc__cross-name';title.textContent=action==='upgrade'?`Upgrade to ${cans} pack`:isRoll?`Add a ${v.title} roll`:`${v.title} · ${cans} pack`;
      const detail=document.createElement('div');detail.className='nxc__cross-meta';detail.textContent=action==='upgrade'?`${v.title} · ${plan?plan.name:'One time'}`:isRoll?(ROLL_NEXT[rolls] || `5 tins · ${plan?plan.name:'One time'}`):`Try another flavor · ${plan?plan.name:'One time'}`;
      info.append(title,detail);
      const control=button('',action,{key:source.key,variant:String(v.id),plan:plan?.id || '',handle:p.handle,price:String(price)});
      control.className='nxc__cross-add';
      if(action==='upgrade') {
        const diff=(price-source.final_price)*source.quantity;
        control.textContent=`+${money(diff)}`;
        control.setAttribute('aria-label',`Upgrade to the ${cans} pack for ${money(diff)} more`);
      }else {
        const priceLabel=document.createElement('div');priceLabel.className='nxc__cross-price';priceLabel.textContent=money(price);info.append(priceLabel);
        const icon=document.createElement('span');icon.setAttribute('aria-hidden','true');icon.style.display='flex';
        icon.innerHTML='<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
        const label=document.createElement('span');label.textContent='Add';
        control.append(icon,label);
        control.setAttribute('aria-label',isRoll?`Add a ${v.title} roll for ${money(price)}`:`Add the ${v.title} ${cans} pack for ${money(price)}`);
      }
      card.append(info,control);
      shelf().append(card);
    }
    if(rolls) {
      // Prefer a flavour the bag does not have yet; otherwise another roll of the first one.
      const p=await product(ROLL_HANDLE);
      const inBag=new Set(cart.items.filter(i=>i.handle===ROLL_HANDLE).map(i=>i.variant_title));
      const v=p.variants.find(v=>v.available&&!inBag.has(v.title)) || p.variants.find(v=>v.available&&v.title===flavor);
      if(el.isConnected)offer(p,v,'cross');
      return;
    }
    if(next) {const p=await product(next);offer(p,p.variants.find(v=>v.title===flavor),'upgrade');}
    const p=await product(PACK_HANDLES[0]);
    const flavors=new Set(cart.items.filter(i=>i.handle.startsWith('nurox-focus-pouches')).map(i=>i.variant_title));
    const variant=p.variants.find(v=>['Blueberry','Peach','Wintergreen'].includes(v.title)&&v.available&&!flavors.has(v.title));
    if(el.isConnected)offer(p,variant,'cross');
  }catch {el.dataset.mounted='';}
}
function mount() {
  // A drawer re-render can copy an already built shelf back into a host that still counts as
  // mounted, so the per-element guard cannot catch it. Collapsing here runs on every observer
  // pass instead, and removing a node settles on the next pass because one shelf removes nothing.
  for(const el of document.querySelectorAll('[data-nurox-cart-offers]')) {
    const shelves=el.querySelectorAll('.nxc__cross');
    for(let i=1;i<shelves.length;i++) shelves[i].remove();
  }
  document.querySelectorAll('[data-nurox-cart-line]').forEach(mountLine);
  document.querySelectorAll('[data-nurox-cart-offers]').forEach(mountOffers);
}
async function act(el) {
  if(busy)return;busy=true;el.disabled=true;document.querySelectorAll('cart-items-component').forEach(node=>node.classList.add('cart-items-disabled'));notice('Updating your bag…');
  try {
    const cart=await json('cart.js');
    const key=el.dataset.key || el.closest('[data-nurox-cart-line]')?.dataset.key;
    const source=cart.items.find(i=>i.key===key);
    if(!source)throw new Error('Your bag changed. Please reopen it and try again.');
    let result;
    if(['subscription','cadence'].includes(el.dataset.cartAction)) {
      // 'subscription' carries the default plan on the switch; 'cadence' is the select itself.
      const plan=el.dataset.cartAction==='cadence'?el.value:el.dataset.plan;
      result=await change({id:source.key,quantity:source.quantity,selling_plan:plan});
    }else {
      // Add first: a rejected or unavailable upgrade must never delete the original pack.
      result=await add({id:el.dataset.variant,quantity:el.dataset.cartAction==='upgrade'?source.quantity:1,selling_plan:el.dataset.plan || undefined,properties:el.dataset.cartAction==='upgrade'?source.properties:undefined});
      if(el.dataset.cartAction==='upgrade') {
        try {result=await change({id:source.key,quantity:0});}
        catch {paint(result);throw new Error('The larger pack was added, but the original pack could not be removed. Please review both items before checkout.');}
      }
    }
    if(result.item_count===undefined)result={...await json('cart.js'),sections:result.sections};
    paint(result);notice('Bag updated.');
  }catch(error){notice(error.message);}
  finally{busy=false;el.disabled=false;document.querySelectorAll('cart-items-component').forEach(node=>node.classList.remove('cart-items-disabled'));mount();}
}
document.addEventListener('click',event=>{const el=event.target.closest('button[data-cart-action]');if(el){event.preventDefault();act(el);}});
document.addEventListener('change',event=>{if(event.target.matches('select[data-cart-action="cadence"]'))act(event.target);});
let queued=false;
new MutationObserver(()=>{if(queued)return;queued=true;queueMicrotask(()=>{queued=false;mount();});}).observe(document.body,{childList:true,subtree:true});
mount();
const style=document.createElement('style');style.textContent=`
.cart-items__table-row > .nxc__sub-cell{grid-column:1 / -1;width:auto !important;min-width:0}
[data-nurox-cart-line]{display:grid;gap:8px;width:100%;padding-top:8px}
.nxc__action,.nxc__cadence{font:inherit;font-size:12px;min-height:42px;border:1px solid #355a1d;border-radius:3px;padding:10px 12px;background:#10150d;color:#f5f5f5;text-align:left;width:100%}
.nxc__action{color:#80ff00;cursor:pointer}.nxc__action[aria-pressed=true]{border-color:#80ff00}.nxc__action:hover{background:#1d2b14}.nxc__action:disabled{opacity:.55;cursor:wait}
.nxc__cadence{color-scheme:dark;min-height:36px;padding:6px 9px}.nxc__cadence[hidden]{display:none}
.nxc__subscription-details[hidden]{display:none}
[data-nurox-cart-line] .nxc__sub-toggle{font-size:11px !important;gap:6px !important;padding:10px 8px !important;min-height:40px}
[data-nurox-cart-line] .nxc__sub-toggle-text{letter-spacing:0}
[data-nurox-cart-line] .nxc__sub-switch{width:26px}
.nxc__subscription-details small{display:block;color:#aaa;font-size:10px;line-height:1.4;margin:5px 0 0}
[data-nurox-cart-status]{font-size:12px;line-height:1.5;color:#eee}[data-nurox-cart-status]:empty{display:none}
.nxc__action:focus-visible,.nxc__cadence:focus-visible{outline:2px solid #80ff00;outline-offset:3px}
`;document.head.append(style);
